// import * as Sentry from '@sentry/browser';
import camelcaseKeys from 'camelcase-keys';
import * as Sentry from '@sentry/browser';
import * as lib from '~/dependencies';
import { getToken } from '~/helpers';
import { PUBLIC_API_BASE_URL, UPDATER_CONFIG } from '~/config/config';

export function getEnvFromWindowHref() {
  if (window.location.href.includes('d2046zq7ft8yun')) return 'PROD';
  if (window.location.href.includes('d1gaikb7mx9zak')) return 'DEV';
  if (window.location.href.includes('djhk1kho598u6')) return 'RC';
  if (window.location.href.includes('localhost')) return 'LOCALHOST';
  return 'PROD';
}

// URL BASE
const axiosBaseUrl = lib.axios.create({
  baseURL: PUBLIC_API_BASE_URL,
  timeout: 30000,
});

const updateCheckerUrl = lib.axios.create({
  baseURL: UPDATER_CONFIG[getEnvFromWindowHref()].UPDATE_CHECKER_URL,
  headers: {
    'Cache-Control': 'no-cache',
  },
});

// API used to get the queue status
export const axiosBaseUrlWss = lib.axios.create({
  baseURL: 'https://wss-queues.nuacom.net',
  timeout: 30000,
});

axiosBaseUrlWss.interceptors.request.use(config => {
  const configuration = config;

  configuration.headers = {
    'Client-Security-Token': getToken(),
    'Content-Type': 'application/json',
  };

  return config;
});

// Refer to dynamic integration URLs
const url4 = lib.axios.create();

// Refer to dynamic URLs for integrations
url4.interceptors.request.use(config => {
  const token = getToken();
  const configuration = config;

  configuration.headers = {
    'Client-Security-Token': token,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  return configuration;
});

axiosBaseUrl.interceptors.request.use(config => {
  const token = getToken();

  config.headers = config.headers || {};

  config.headers = {
    ...config.headers,
    'Client-Security-Token': token,
    Accept: 'application/json',
  };

  // Added for backward compatibility since some APIs may still require it, though unspecified.
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  const isLogin = /\/login|\/credential|\/sso|\/forgot_login/.test(config?.url);

  if (!token && !isLogin) {
    Sentry.captureMessage('nuacom_log: token is missing', Sentry.Severity.Info);
  }

  return config;
});

function createSentry401ReportRedirectToSignIn() {
  Sentry.captureMessage(
    'NUACOM_LOG: Destkop Softphone logged out as result of unauthorized access. Status Code 401.',
    Sentry.Severity.Info
  );

  window.location = '/logout';
}

// NOTE: List of urls which response data will be automatically camelcased
const autoCamelCaseUrlList = [
  '/v2/account-queue-headset',
  '/queues/membership',
  '/v2/integrations/gohighlevel',
  '/v2/auth/login',
  '/v2/sso/google/token?code=',
  '/v2/sso/microsoft/token?code=',
  '/v2/sms-templates',
  '/v2/departments',
  'v2/conversations/unread-count',
  '/v2/users_list',
  '/v2/sms/sender_names',
  '/v2/conversations',
  '/mapped-numbers',
];

axiosBaseUrl.interceptors.response.use(
  response => {
    if (response.status === 401) {
      createSentry401ReportRedirectToSignIn();
    }

    if (
      response.status === 200 &&
      autoCamelCaseUrlList.some(urlItem =>
        response.config.url.includes(urlItem)
      )
    ) {
      response.data = camelcaseKeys(response.data, { deep: true });
    }

    return response;
  },
  error => {
    const isLogin = /\/login|\/credential|\/sso|\/forgot_login/.test(
      error?.response?.config.url
    );

    if (error?.response?.status === 401 && !isLogin) {
      createSentry401ReportRedirectToSignIn();
    }

    return Promise.reject(error);
  }
);

axiosBaseUrl.interceptors.response.use(response => {
  if (
    response.data &&
    autoCamelCaseUrlList.some(urlItem => response.config.url.includes(urlItem))
  ) {
    response.data = camelcaseKeys(response.data, { deep: true });
  }

  return response;
});

const extraHeaders = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const api = {
  auth: {
    get: {
      authSSOSecureCode: (provider, code) =>
        axiosBaseUrl.get(`/v2/sso/${provider}/token?code=${code}`),
    },
    post: {
      revokeToken: () => axiosBaseUrl.post(`/v2/logout`),
    },
  },
  contacts: {
    get: {
      list: (type = 'All', page) =>
        axiosBaseUrl.get(
          `/v1/contacts${type === 'Personal' ? '?personal=true' : ''}${
            type === 'Personal' ? `&page=${page}` : `?page=${page}`
          }&sort=contact_name&requested_time=1`
        ),
      checkUpdates: (date, personal) =>
        axiosBaseUrl.get(
          `/v1/contacts?last_updated=${date}&deleted=true&personal=${personal}&sort=contact_name&requested_time=1`
        ),
      contactInfo: id => axiosBaseUrl.get(`/v1/contacts/${id}`),
      byNumber: number => axiosBaseUrl.get(`/v1/contacts?q=${number}`),
    },
    post: {
      newContact: data => axiosBaseUrl.post(`/v1/contacts`, data),
    },
    patch: {
      updateContact: (id, data) =>
        axiosBaseUrl.patch(`/v1/contacts/${id}`, data),
    },
    delete: {
      deleteContact: id => axiosBaseUrl.delete(`/v1/contacts/${id}`),
    },
    groups: {
      list: () => axiosBaseUrl.get(`/v1/groups`),
      create: ({ name }) =>
        axiosBaseUrl.post(`/v1/groups`, { group_name: name }),
    },
    organization: {
      list: () => axiosBaseUrl.get(`/v1/organizations`),
      create: ({ name }) => axiosBaseUrl.post(`/v1/organizations`, { name }),
    },
  },
  callNotes: {
    get: {
      noteInfo: (callId, sipCallId, originalSipCallId) =>
        axiosBaseUrl.get(
          `/v2/call-notes?callIDs[]=${callId}${
            sipCallId ? `&callIDs[]=${sipCallId}` : ''
          }${originalSipCallId ? `&callIDs[]=${originalSipCallId}` : ''}`
        ),
    },
    post: {
      registerNote: data => axiosBaseUrl.post(`/v1/call_note`, data),
    },
  },
  calls: {
    get: {
      logsWithFilterOptions: customFiltersQuery =>
        axiosBaseUrl.get(`/v2/call-log?${customFiltersQuery}`),
      logsWithDate: (date, extension) =>
        axiosBaseUrl.get(
          `/v1/call_logs?from_date=${date}&extensions=${extension}`
        ),
      logDetail: id => axiosBaseUrl.get(`/v1/call_logs/${id}`),
      record: (callId, format) =>
        axiosBaseUrl.get(`/v1/recording?call_id=${callId}&format=${format}`, {
          responseType: 'blob',
        }),
      logsByPhoneNumber: (number, limit, fromDate, toDate, config) =>
        axiosBaseUrl.get(
          `/v2/call-log?per_page=${limit}&number=${number}&from_date=${fromDate}&to_date=${toDate}&sort_by=call_date&sort_dir=desc`,
          config
        ),
    },
    delete: {
      record: callId => axiosBaseUrl.delete(`/v2/call-recording/${callId}`),
    },
  },
  accounts: {
    get: {
      accountInfo: () => axiosBaseUrl.get(`/v1/account_info`),
      extensionInfo: () => axiosBaseUrl.get(`/v1/extension_info`),
      allExtensions: () => axiosBaseUrl.get(`/v1/extensions`),
      allowedExtensions: () => axiosBaseUrl.get(`/v2/extensions`),
      outboundNumbers: () =>
        axiosBaseUrl.get(`/v2/extensions/user-info-and-available-cli`),
      departments: () => axiosBaseUrl.get(`/v2/departments`),
      smsSenders: () =>
        axiosBaseUrl.get(`/v2/sms/sender_names?filter[channel]=sms`),
      whatsappSenders: () =>
        axiosBaseUrl.get(`/v2/sms/sender_names?filter[channel]=whatsapp`),
      getUserList: () => axiosBaseUrl.get(`/v2/users_list?includeStaff=1`),
      getTemplates: (page, perPage) =>
        axiosBaseUrl.get(
          `/v2/sms-templates?sort=name&per_page=${perPage}&page=${page}&additional=campaign_use_cases&filter[status]=1&filter[channel]=1,2&sort=name`
        ),
      featureFlags: () => axiosBaseUrl.get(`/new/feature-flags/available`),
    },
    post: {
      outboundNumberChange: outboundNumber =>
        axiosBaseUrl.post(`/v1/user_info_cli`, { number: outboundNumber }),
      setExtension: data => axiosBaseUrl.post(`/v1/user_info_extension`, data),
    },
  },
  tags: {
    get: {
      allTags: () => axiosBaseUrl.get(`/v1/call_tag_info`),
      getTag: callId => axiosBaseUrl.get(`/v1/call_tag/${callId}`),
    },
    post: {
      createTag: data => axiosBaseUrl.post(`/v1/call_tag_info`, data),
      addCallTag: data => axiosBaseUrl.post(`/v1/call_tag`, data),
    },
    patch: {
      updateTag: data => axiosBaseUrl.patch(`/v1/call_tag_info`, data),
    },
    delete: {
      tag: data =>
        axiosBaseUrl.delete(`/v1/call_tag_info`, { extraHeaders, data }),
      callTag: data =>
        axiosBaseUrl.delete(`/v1/call_tag`, { extraHeaders, data }),
    },
    fetchCallTags: (callId, callIdTwo, originalSipCallId, config) =>
      axiosBaseUrl.get(
        `/v2/call-tags?callIDs[]=${callId}&callIDs[]=${callIdTwo || callId}${
          originalSipCallId ? `&callIDs[]=${originalSipCallId}` : ''
        }`,
        config
      ),
    fetchTagsDetails: (availableFor, config) =>
      axiosBaseUrl.get(
        `/v3/call-tags?page=1&perPage=100&filter[available_for]=${availableFor}`,
        config
      ),
  },
  queues: {
    get: {
      allQueues: () => axiosBaseUrl.get(`/v2/queues`),
      dynamicQueues: () => axiosBaseUrl.get(`/v2/account-queue-headset`),
      dynamicQueueStatus: (accountId, extension) =>
        axiosBaseUrlWss.get(`/queues/membership/${accountId}/${extension}`),
    },
    post: {
      setAgentStatus: data =>
        axiosBaseUrl.post(
          `/v2/extensions/set-dynamic-agent-queue-status`,
          data
        ),
    },
  },
  users: {
    get: {
      userInfo: () => axiosBaseUrl.get(`/v1/user_info`),
      getCredentials: token => axiosBaseUrl.get(`/credential/${token}`),
      favoriteAgents: () => axiosBaseUrl.get(`/v1/user_favourite_agents`),
    },
    post: {
      login: (email, pass) =>
        axiosBaseUrl.post(`/v2/auth/login`, {
          email,
          pass,
          remember: true,
        }),
      authSecureCode: (email, password, secureCode) =>
        axiosBaseUrl.post(`/v2/auth/login`, {
          email,
          pass: password,
          two_fa_code: secureCode,
          remember: true,
        }),
      changeAccount: account =>
        axiosBaseUrl.post(`/v1/user_info_change_account`, {
          user_account: account,
        }),
      recoverPassword: email =>
        axiosBaseUrl.get(`/forgot_login?email=${email}`),
      changePassword: (email, password, newPassword, newPasswordConfirmation) =>
        axiosBaseUrl.post(`/v2/auth/update-password`, {
          email,
          password,
          new_password: newPassword,
          new_password_confirmation: newPasswordConfirmation,
        }),
      updateFavoriteAgents: data =>
        axiosBaseUrl.post(`/v1/user_favourite_agents`, data),
    },
  },
  feedback: {
    post: {
      sendFeedback: data => axiosBaseUrl.post(`/v2/feedback`, data),
    },
  },
  system: {
    checkUpdates: () => updateCheckerUrl.get(),
  },
  sms: {
    post: {
      /**
       * @deprecated This method will be removed in v1.26.0 as conversations feature deprecated SendSMS component.
       */
      sendSMS: (data, config) => axiosBaseUrl.post(`/v1/sms`, data, config),
      changeSmsBillType: (data, config) =>
        axiosBaseUrl.post(`/v1/sms_change_billing_to `, data, config),
    },
    get: {
      smsBillPayRate: () => axiosBaseUrl.get(`/v1/sms_billpay_rate`),
      /**
       * @deprecated This method will be removed in v1.26.0 as conversations feature deprecated SendSMS component.
       */
      smsSenderNames: () => axiosBaseUrl.get(`/v1/sms_sender_names`),
    },
  },
  announcements: {
    get: {
      all: () => axiosBaseUrl.get(`/v2/softphone-announcements`),
      byId: id =>
        axiosBaseUrl.get(`/v2/softphone-announcements/${id}/audio`, {
          responseType: 'blob',
        }),
    },
  },
  integrations: {
    get: {
      accountIntegrations: () =>
        axiosBaseUrl.get(`/v2/integrations/list-active-integrations`),
      contactInfoByNumber: number =>
        axiosBaseUrl.get(`/v2/integrations/contacts?phone=${number}`),
      contactOnwerByExternalId: externalId =>
        axiosBaseUrl.get(
          `/v1/integrations/contact_owner?external_id=${externalId}`
        ),
      getTickets: (endpoint, externalId, callId, config) =>
        axiosBaseUrl.get(
          `/v2/integrations/${endpoint}?external_id=${externalId}&call_id=${callId}`,
          { ...config }
        ),
      getTicketFields: config =>
        axiosBaseUrl.get(`/v2/integrations/auto_task/ticket-fields`, {
          ...config,
        }),
      getAutoTaskCompanies: config =>
        axiosBaseUrl.get(`/v2/integrations/auto_task/companies`, {
          ...config,
        }),
      /**
       * @param {string} name company name
       * @param {boolean} leads It should include company leads in the searching?!
       */
      getAutoTaskCompanyByName: (name, leads, config) => {
        // FIXME: As describled on NWA-1021, we should be able to remove this approach soon, keeping `&leads=leads` only.
        const leadsQuery = leads ? `&leads=${leads}` : ``;
        return axiosBaseUrl.get(
          `/v2/integrations/auto_task/companies?name=${name}${leadsQuery}`,
          {
            ...config,
          }
        );
      },
      contactsByUri: retrieveContactsUri => url4.get(retrieveContactsUri),
      getCustomUrl: number =>
        axiosBaseUrl.get(`/v2/integrations/custom-urls/get-urls/${number}`),
      goHighLevelLastMessages: (accountId, contactId) =>
        axiosBaseUrl.get(
          `/v2/integrations/gohighlevel/${accountId}/contacts/${contactId}/last-messages`
        ),
      goHighLevelGetMappedNumbers: accountId =>
        axiosBaseUrl.get(
          `/v2/integrations/gohighlevel/${accountId}/mapped-numbers`
        ),
    },
    post: {
      createContact: (endpoint, data) => url4.post(endpoint, data),
      createTicket: (
        endpoint,
        externalId,
        callId,
        title,
        description,
        status,
        priority,
        queue
      ) =>
        axiosBaseUrl.post(`/v2/integrations/${endpoint}`, {
          external_id: externalId,
          call_id: callId,
          title,
          description,
          status,
          priority,
          queue,
        }),
      byDynamicURI: (endpoint, data) =>
        axiosBaseUrl.post(`/v2/integrations/${endpoint}`, data),
      /**
       * Contacts which will receive the call activity on Salesforce.
       * https://nuacom.atlassian.net/wiki/spaces/WA/pages/1980792877/Salesforce+Integration
       */
      syncCallWithCRMContacts: data =>
        axiosBaseUrl.post(`/v2/integrations/bulk-link-call-contact`, data),
      unSyncCallForCRMContacts: data =>
        axiosBaseUrl.post(`/v2/integrations/bulk-unlink-call-contact`, data),
    },
    put: {
      starConversation: (accountId, conversationId) =>
        axiosBaseUrl.put(
          `/v2/integrations/gohighlevel/${accountId}/conversations/${conversationId}/star`
        ),
      unmarkStarConversation: (accountId, conversationId) =>
        axiosBaseUrl.put(
          `/v2/integrations/gohighlevel/${accountId}/conversations/${conversationId}/unstar`
        ),
    },
  },
  afterCallWork: {
    get: {
      rules: () => axiosBaseUrl.get(`/v2/after-call-work`),
    },
  },
  conversation: {
    get: {
      getConversations: options =>
        axiosBaseUrl.get(`/v2/conversations`, options),
      getConversationById: id => axiosBaseUrl.get(`/v2/conversations/${id}`),
      getUnreadMessagesCount: () =>
        axiosBaseUrl.get(`/v2/conversations/unread-count`),
    },
    post: {
      createConversation: conversation =>
        axiosBaseUrl.post(`/v2/conversations`, conversation),
      sendMessage: message =>
        axiosBaseUrl.post(`/v2/conversations/messages`, message),
      addMessageToConversation: (conversationId, message) =>
        axiosBaseUrl.post(
          `/v2/conversations/${conversationId}/messages`,
          message
        ),
    },
    patch: {
      archive: conversationId =>
        axiosBaseUrl.patch(`/v2/conversations/${conversationId}/archive`),
      pin: conversationId =>
        axiosBaseUrl.patch(`/v2/conversations/${conversationId}/pin`),
      assign: (conversationId, data) =>
        axiosBaseUrl.patch(`/v2/conversations/${conversationId}/assign`, data),
      readStatus: (conversationId, data) =>
        axiosBaseUrl.patch(
          `/v2/conversations/${conversationId}/read-status`,
          data
        ),
    },
    delete: {
      cancelMessage: (conversationId, messageId) =>
        axiosBaseUrl.delete(
          `/v2/conversations/${conversationId}/messages/${messageId}`
        ),
    },
  },
};

export default api;
